/**
 * GIWAHS — Auth Context
 * ---------------------------------------------------------------------
 * Single source of truth for `user`, `session`, `loading`, and the
 * auth action surface (signUp, signIn, signOut, requestPasswordReset,
 * updatePassword). Wraps the mode-switch authClient.
 *
 * Components must read auth state via the useAuth() hook; never import
 * authClient directly from UI code.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { authClient } from '../lib/authClient';
import { track, EVENTS } from '../lib/analytics';
import { dispatch } from '../lib/notifications';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const mountedRef            = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let unsubscribe = () => {};

    (async () => {
      try {
        const s = await authClient.getSession();
        if (!mountedRef.current) return;
        setSession(s || null);
        setUser(s?.user || null);
      } catch {
        if (mountedRef.current) { setSession(null); setUser(null); }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
      unsubscribe = authClient.onAuthStateChange((event, newSession) => {
        if (!mountedRef.current) return;
        setSession(newSession || null);
        setUser(newSession?.user || null);
      });
    })();

    return () => { mountedRef.current = false; try { unsubscribe(); } catch {} };
  }, []);

  const signUp = useCallback(async (email, password) => {
    const result = await authClient.signUp({ email, password });
    // Fire only on explicit user action — never from session restore.
    track(EVENTS.AUTH_SIGNED_UP, {
      auth_mode: authClient.type,
      account_type: 'buyer',
      needs_confirmation: !!result?.needsConfirmation,
    });
    // Welcome email — fire-and-forget, mock mode logs only.
    dispatch({
      kind: 'signup_welcome',
      to: email,
      payload: { email, created_at: new Date().toISOString().slice(0, 10) },
    });
    return result;
  }, []);

  const signIn = useCallback(async (email, password) => {
    const result = await authClient.signIn({ email, password });
    track(EVENTS.AUTH_SIGNED_IN, {
      auth_mode: authClient.type,
      account_type: result?.user?.account_type || result?.user?.user_metadata?.account_type || 'buyer',
    });
    return result;
  }, []);

  const signOut = useCallback(async () => {
    const mode = authClient.type;
    await authClient.signOut();
    setUser(null); setSession(null);
    track(EVENTS.AUTH_SIGNED_OUT, { auth_mode: mode });
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    await authClient.requestPasswordReset(email);
    track(EVENTS.AUTH_PASSWORD_RESET_REQUESTED, {
      auth_mode: authClient.type,
      account_type: 'buyer',
    });
  }, []);

  const updatePassword = useCallback(async (newPassword) => {
    const result = await authClient.updatePassword(newPassword);
    track(EVENTS.AUTH_PASSWORD_RESET_COMPLETED, {
      auth_mode: authClient.type,
      account_type: 'buyer',
      via: 'session_link',
    });
    return result;
  }, []);

  /** Mock-mode only — completes the reset via the email captured by requestPasswordReset. */
  const completeMockPasswordReset = useCallback(async (email, password) => {
    if (authClient.type !== 'mock') throw new Error('Only available in mock mode.');
    const result = await authClient.updatePasswordWithMockToken({ email, password });
    track(EVENTS.AUTH_PASSWORD_RESET_COMPLETED, {
      auth_mode: 'mock',
      account_type: 'buyer',
      via: 'manual_form',
    });
    track(EVENTS.AUTH_SIGNED_IN, { auth_mode: 'mock', account_type: 'buyer', via: 'password_reset' });
    return result;
  }, []);

  const resendConfirmation = useCallback(async (email) => {
    const result = await authClient.resendConfirmation(email);
    track(EVENTS.AUTH_CONFIRMATION_RESENT, {
      auth_mode: authClient.type,
      account_type: 'buyer',
      skipped: !!result?.skipped,
    });
    return result;
  }, []);

  const value = useMemo(() => ({
    user, session, loading,
    isAuthenticated: !!user,
    authMode: authClient.type,
    signUp, signIn, signOut,
    requestPasswordReset, updatePassword,
    completeMockPasswordReset, resendConfirmation,
  }), [user, session, loading, signUp, signIn, signOut, requestPasswordReset, updatePassword, completeMockPasswordReset, resendConfirmation]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an AuthProvider');
  return ctx;
};
